import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Loader2, ChevronDown } from "lucide-react";
import DomainElements from "@/components/gsa/DomainElements";
import { Domain } from "@/types/domain";
import KnowledgeNetworkStats from "@/components/gsa/KnowledgeNetworkStats";
import CircularDomainDial from "@/components/gsa/CircularDomainDial";
import TeamMembersGrid from "@/components/ispai/TeamMembersGrid";

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useUsaNetworkStats } from "@/hooks/useUsaNetworkStats";
import { InviteColleagueDialog } from "@/components/InviteColleagueDialog";

// PBS Domain Elements (6 domains for CircularDomainDial)
const pbsDomains = [
  { name: "Design Automation", color: "bg-blue-600" },
  { name: "Space and Furniture Layout", color: "bg-blue-600" },
  { name: "Analysis Tools", color: "bg-blue-600" },
  { name: "Cost and Schedule Estimation", color: "bg-blue-600" },
  { name: "Visualization & 3D Rendering", color: "bg-blue-600" },
  { name: "Post-Occupancy Monitoring", color: "bg-blue-600" }
];

// GSA-wide Domain Elements (15 domains for main DomainElements section)
const gsaDomains: Domain[] = [
  {
    symbol: "Federal Procurement",
    name: "Federal Procurement and Contracting",
    categories: "Government-wide Acquisition Contracts (GWACs), Multiple Award Schedules (MAS), Simplified Acquisition Procedures (SAP), Federal Supply Schedules (FSS), Procurement Integrity and Compliance, Contractor Performance Evaluation"
  },
  {
    symbol: "Real Estate",
    name: "Real Estate and Public Buildings",
    categories: "Federal Property Management, Lease Administration and Negotiation, Sustainable Building Standards, Public Building Lifecycle Management, Historic Property Preservation, Space Optimization and Utilization"
  },
  {
    symbol: "IT",
    name: "IT and Digital Transformation",
    categories: "Cloud Adoption for Federal Agencies, Government-wide Cybersecurity Frameworks, AI and Automation in Federal Operations, Data Governance and Privacy in GSA, IT Modernization Initiatives, Digital Services Procurement"
  },
  {
    symbol: "Fleet",
    name: "Fleet Management and Transportation Services",
    categories: "Federal Vehicle Procurement, Electric and Hybrid Fleet Transition, GPS and Telematics for Government Vehicles, Federal Transportation Cost Optimization, Maintenance and Lifecycle Management, Public Sector Supply Chain Logistics"
  },
  {
    symbol: "Supply Chain",
    name: "Supply Chain and Logistics Management",
    categories: "Federal Supply Chain Resilience, Warehouse and Inventory Management, Strategic Sourcing for Government Agencies, Global Supply Chain Risk Mitigation, Vendor Management and Acquisition, Logistics and Distribution Optimization"
  },
  {
    symbol: "P3",
    name: "Public-Private Partnerships (P3)",
    categories: "Federal Infrastructure Investment Models, P3 Contracting and Compliance, Risk-sharing Mechanisms in Federal Projects, Alternative Funding for Federal Programs, Performance-based Contracting"
  },
  {
    symbol: "Workforce",
    name: "Federal Workforce Management",
    categories: "Human Capital Strategy in Federal Agencies, Federal Employee Training and Upskilling, DEI Initiatives and Compliance, Remote and Hybrid Work Policies, Leadership Development for Public Sector"
  },
  {
    symbol: "Energy",
    name: "Energy and Sustainability Initiatives",
    categories: "Green Procurement Policies, Net Zero Energy in Federal Buildings, Energy Performance Contracting (EPC), Climate Resilience in Federal Infrastructure, Renewable Energy Adoption in GSA"
  },
  {
    symbol: "Grants",
    name: "Federal Grants and Funding Programs",
    categories: "Grant Application and Compliance, Federal Economic Stimulus Packages, Disaster Recovery and FEMA Grants, Cooperative Agreements and Cost-sharing, Grant Program Impact Assessments"
  },
  {
    symbol: "Emergency",
    name: "Emergency Preparedness and Continuity of Operations",
    categories: "Federal Disaster Response Planning, Government Resilience Frameworks, Emergency Procurement Procedures, Cybersecurity and Infrastructure Resilience, Continuity of Operations (COOP) Planning"
  },
  {
    symbol: "Travel",
    name: "Federal Travel and Expense Management",
    categories: "Per Diem and Travel Reimbursement Guidelines, Federal Employee Travel Programs, Contracting for Travel Management Services, Travel Policy Compliance Audits, Government Conference and Event Planning"
  },
  {
    symbol: "Smart Cities",
    name: "GSA Smart Cities and Urban Innovation",
    categories: "Federal Smart City Strategies, IoT Applications for Public Infrastructure, Smart Energy Grid for Federal Buildings, Digital Identity Management for Public Services, Public Transportation Modernization"
  },
  {
    symbol: "Cybersecurity",
    name: "Public Sector Cybersecurity and Risk Management",
    categories: "Federal Data Protection Standards, Identity and Access Management (IAM), Continuous Monitoring and Threat Intelligence, Cloud Security for Federal Systems, Cyber Risk and Compliance for Government Vendors"
  },
  {
    symbol: "Vendor",
    name: "GSA Vendor and Industry Engagement",
    categories: "Industry Days and Federal Market Research, Vendor Prequalification and Certification, Small Business Contracting Strategies, GSA Schedule Pricing Analysis, Innovation Procurement Models"
  },
  {
    symbol: "Open Data",
    name: "Government Data and Open Access Initiatives",
    categories: "Federal Data Sharing Agreements, Open Government Data Strategies, AI-driven Analytics for Public Services, Cloud-based Data Warehousing, GIS and Geospatial Intelligence for Public Use"
  }
];

const UsaChannel = () => {
  const [deptOpen, setDeptOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { data: usaNetworkStats, isLoading } = useUsaNetworkStats();


  const usaTeamMembers = [
    {
      image: "/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png",
      alt: "Julia De Rosi"
    },
    {
      image: "/gibniftys/heather-edwards.png",
      alt: "Heather Edwards"
    },
    {
      image: "/gibniftys/charles-hardy.png",
      alt: "Charles Hardy"
    }
  ];
  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <img 
                        src="/lovable-uploads/gsa-logo.png" 
                        alt="GSA Logo" 
                        className="w-20 h-20 object-contain cursor-pointer hover:scale-110 transition-transform" 
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>GSA Channel</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <div>
                  <CardTitle className="text-2xl font-bold mb-1">
                    Welcome to the U.S. National Knowledge Network
                  </CardTitle>
                  <CardDescription className="text-base mb-2">
                    GSA Dashboard
                  </CardDescription>
                  
                  {/* Agency Giblets */}
                  <div className="flex items-center gap-3 mt-2">
                    <Link to="/gsa-channel">
                      <img 
                        src="/lovable-uploads/gsa-giblet.png" 
                        alt="GSA Giblet" 
                        className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" 
                        title="General Services Administration" 
                      />
                    </Link>
                    <Link to="/dept-education-channel">
                      <img 
                        src="/lovable-uploads/dept-education-giblet.png" 
                        alt="Department of Education Giblet" 
                        className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" 
                        title="Department of Education" 
                      />
                    </Link>
                    <Link to="/noaa-channel">
                      <img 
                        src="/lovable-uploads/noaa-giblet.png" 
                        alt="NOAA Giblet" 
                        className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" 
                        title="National Oceanic and Atmospheric Administration" 
                      />
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/gsa-call-for-solutions">
                        <Button 
                          className="bg-black text-white hover:bg-[#1e603b] transition-colors"
                          size="sm"
                        >
                          CfS
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Solution (demo)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/gsa-call-for-destruction">
                        <Button 
                          className="bg-black text-white hover:bg-[#1e603b] transition-colors"
                          size="sm"
                        >
                          CfD
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Destruction (demo)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Team Profiles */}
            <div className="mb-8">
              <TeamMembersGrid 
                members={usaTeamMembers}
                showAskCurt={false}
                useProfileAvatars={true}
                channelId="536ba1bb-4a0a-4024-a19c-f77b566bd860"
              />
            </div>

            {/* Network Stats - Full Width */}
            <div className="mb-8">
              {isLoading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : usaNetworkStats ? (
                <KnowledgeNetworkStats stats={usaNetworkStats} />
              ) : (
                <div className="text-center p-12 text-muted-foreground">
                  No data available
                </div>
              )}
            </div>

            {/* GSA Participation by Department - Collapsible */}
            {usaNetworkStats && usaNetworkStats.departments && usaNetworkStats.departments.length > 0 && (
              <div className="mt-8 mb-8">
                <Collapsible open={deptOpen} onOpenChange={setDeptOpen}>
                  <CollapsibleTrigger className="flex items-center gap-2 font-semibold text-xl mb-4 hover:text-primary transition-colors">
                    <ChevronDown className={`h-5 w-5 transition-transform ${deptOpen ? 'rotate-180' : ''}`} />
                    GSA Participation by Department
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-3">
                      {usaNetworkStats.departments.map((dept, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <span className="font-medium">{dept.name}</span>
                          <span className="text-sm text-muted-foreground">{dept.participants} participants</span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

            {/* PBS Domain Elements - Single Row */}
            <div className="mt-8 mb-8">
              <CircularDomainDial domains={pbsDomains} />
            </div>

            {/* GSA Domain Elements */}
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">GSA Domain Elements</h2>
              <DomainElements domains={gsaDomains} />
            </div>

            {/* iSPAI Industry Template */}
            <div className="mt-8 max-w-sm">
              <a 
                href="https://img1.wsimg.com/blobby/go/70e764c8-c764-4e83-ada3-5ccb3a393f3d/downloads/5add6269-a319-4fc0-9dcb-95a52a6c7b5a/iSPAI%20Industry_GSA.pdf?ver=1741886570881" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="transition-transform hover:scale-105 block"
              >
                <Card className="p-3 bg-white">
                  <h4 className="text-sm font-semibold mb-2">iSPAI</h4>
                  <img 
                    src="/lovable-uploads/1055d4ec-0c8f-4663-81f4-ee6eed1dfc9c.png" 
                    alt="Industry Template: U.S. General Services Administration (GSA)" 
                    className="rounded-lg w-full object-contain border border-gray-200" 
                  />
                </Card>
              </a>
            </div>
          </CardContent>
        </Card>
        
        {/* Invite Dialog */}
        <InviteColleagueDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          defaultChannelId="536ba1bb-4a0a-4024-a19c-f77b566bd860"
          defaultChannelName="US Federal Government"
        />
      </main>
    </div>
  );
};

export default UsaChannel;
